import { z } from 'zod';
import { useAuth } from '@/stores/use-auth';
import { getBearerTokenSync } from '@/lib/auth-token';
import { getApiV1RootUrl } from '@/lib/api-config';
import { refreshBrowserSession } from '@/lib/session-refresh';
import { analytics } from './analytics';

/**
 * Normalized backend origin including `/api/v1` (no trailing slash).
 * Use for full-page navigations (OAuth / GitHub App install) that bypass `apiClient`.
 */
export { getApiV1BaseUrl } from '@/lib/api-config';

const API_URL = getApiV1RootUrl();

/** Prevents rapid sign-in ↔ app redirect loops when refresh is still recovering. */
const FORCED_LOGOUT_COOLDOWN_MS = 2000;
let lastForcedLogoutAt = 0;

/** Reset forced-logout throttle (tests). */
export function resetForcedLogoutCooldownForTests(): void {
  lastForcedLogoutAt = 0;
}

export class ApiError extends Error {
  constructor(
    public message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** `410 { error: { code: 'WORK_ITEM_TOMBSTONED' } }` — the ticket was deleted at Jira/Linear. */
export function isWorkItemTombstonedError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 410) return false;
  const code = (err.data as { error?: { code?: unknown } } | undefined)?.error?.code;
  return code === 'WORK_ITEM_TOMBSTONED';
}

interface RequestOptions extends RequestInit {
  schema?: z.ZodSchema;
  _retryAfterRefresh?: boolean;
  _skipAuthRefresh?: boolean;
  /** Prevents `restoreSession()` re-entry when `/users/me` is called from inside `restoreSession()`. */
  _skipNestedSessionRestore?: boolean;
}

function isAuthPagePath(path: string): boolean {
  return ['/signin', '/signup', '/login', '/auth/login'].some((p) => path.includes(p));
}

function shouldSkipRefresh(endpoint: string, options: RequestOptions): boolean {
  if (options._skipAuthRefresh) return true;

  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return (
    normalizedEndpoint.startsWith('/auth/refresh') ||
    normalizedEndpoint.startsWith('/auth/signout') ||
    normalizedEndpoint.startsWith('/auth/dev-login')
  );
}

export async function apiClient<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { schema, ...init } = options;

  // Global settings: credentials and content-type
  const url = `${API_URL}${endpoint.startsWith('/') ? endpoint.substring(1) : endpoint}`;

  // Bearer: Zustand memory (sync) before persisted localStorage; cookies still sent with credentials
  let token: string | null = null;
  try {
    if (typeof window !== 'undefined') {
      token = getBearerTokenSync();
    }
  } catch {
    // Ignore
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (init.headers) {
    Object.assign(headers, init.headers);
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    // Always bypass HTTP cache for API requests to avoid 304 responses with empty bodies
    // which break JSON parsing and React Query data flow.
    credentials: 'include',
    cache: init.cache ?? 'no-store',
    ...init,
    headers,
  });

  // Read body once; supports JSON errors and detects HTML/login pages mis-routed as APIs.
  const rawBody = response.status === 204 || response.status === 205 ? '' : await response.text();

  let data: unknown = null;
  let bodyParseFailed = false;
  if (rawBody.trim()) {
    try {
      data = JSON.parse(rawBody) as unknown;
    } catch {
      bodyParseFailed = true;
      data = null;
    }
  }

  // Global Interceptors
  if (!response.ok) {
    if (response.status === 401) {
      const path = window.location.pathname;
      const isAuthPage = isAuthPagePath(path);
      const isOrgChooser = path === '/choose-organization';

      if (!shouldSkipRefresh(endpoint, options) && !options._retryAfterRefresh) {
        const allowBootstrap = !useAuth.getState().isAuthenticated;
        const refreshed = await refreshBrowserSession({
          trigger: 'reactive',
          force: true,
          allowBootstrap,
        });
        if (refreshed) {
          return apiClient<T>(endpoint, {
            ...options,
            _retryAfterRefresh: true,
          });
        }
      }

      if (!isAuthPage && !isOrgChooser) {
        const { restoreSession, waitForSessionRecovery } = await import('@/lib/session');
        if (!options._skipNestedSessionRestore) {
          await waitForSessionRecovery();
        }
        if (useAuth.getState().isAuthenticated && !options._retryAfterRefresh) {
          return apiClient<T>(endpoint, {
            ...options,
            _retryAfterRefresh: true,
          });
        }

        if (
          !options._retryAfterRefresh &&
          !options._skipNestedSessionRestore &&
          !shouldSkipRefresh(endpoint, options)
        ) {
          if (await restoreSession()) {
            return apiClient<T>(endpoint, {
              ...options,
              _retryAfterRefresh: true,
            });
          }
        }

        if (!options._skipNestedSessionRestore) {
          await waitForSessionRecovery();
        }
        if (useAuth.getState().isAuthenticated && !options._retryAfterRefresh) {
          return apiClient<T>(endpoint, {
            ...options,
            _retryAfterRefresh: true,
          });
        }

        const now = Date.now();
        if (now - lastForcedLogoutAt >= FORCED_LOGOUT_COOLDOWN_MS) {
          lastForcedLogoutAt = now;
          analytics.capture('auth_session_forced_logout', {
            endpoint,
            path,
          });
          useAuth.getState().logout();
        }
      }
    }

    // 403 Forbidden - handle lifecycle-specific codes before generic denial
    if (response.status === 403) {
      const errorCode =
        data && typeof data === 'object' && 'error' in data
          ? (data as { error: string }).error
          : null;

      if (errorCode === 'ORPHANED_USER' || errorCode === 'ORGANIZATION_DEACTIVATED') {
        analytics.capture('account_lifecycle_blocked', { code: errorCode, endpoint });
        // Hard-redirect with replace so the user can't go back to the blocked page.
        // The reason is passed as a query param (api.ts has no access to React Router state).
        window.location.replace(`/account/suspended?reason=${errorCode}`);
        // Throw to stop the caller from processing further — the page is being replaced.
        throw new ApiError(
          errorCode === 'ORPHANED_USER'
            ? 'Your organization no longer exists.'
            : 'This organization has been deactivated.',
          403,
          data
        );
      }

      analytics.capture('access_denied', {
        endpoint,
        method: init.method ?? 'GET',
      });
      console.warn(`Access forbidden: ${endpoint}`);
    }

    // 500+ Internal Server Errors - log to analytics
    if (response.status >= 500) {
      analytics.capture('server_error', {
        status: response.status,
        endpoint,
        method: init.method ?? 'GET',
      });
    }

    const message =
      data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : `API Error ${response.status}`;
    throw new ApiError(message, response.status, data);
  }

  if (bodyParseFailed) {
    const looksLikeHtml = /^\s*</i.test(rawBody);
    const parseException = new SyntaxError(
      looksLikeHtml
        ? "Unexpected token '<' — server returned HTML instead of JSON"
        : 'API response was not valid JSON'
    );
    analytics.captureException(parseException, {
      endpoint,
      method: init.method ?? 'GET',
      status: response.status,
      looks_like_html: looksLikeHtml,
      response_preview_length: rawBody.length,
      ...(import.meta.env.DEV ? { response_preview: rawBody.slice(0, 240) } : {}),
    });
    throw new ApiError(
      looksLikeHtml
        ? 'Server returned a web page instead of JSON. Check API URL (VITE_API_URL) and routing.'
        : 'API returned invalid JSON.',
      response.status,
      { looks_like_html: looksLikeHtml }
    );
  }

  if (schema) {
    const result = schema.safeParse(data);
    if (!result.success) {
      console.error('API Validation Error:', result.error);
      if (import.meta.env.DEV) {
        throw new Error(`Schema validation failed: ${result.error.message}`);
      }
    }
    return (result.success ? result.data : data) as T;
  }

  return data as T;
}

apiClient.get = <T>(endpoint: string, options?: RequestOptions) =>
  apiClient<T>(endpoint, { ...options, method: 'GET' });

apiClient.post = <T>(endpoint: string, body?: unknown, options?: RequestOptions) => {
  const init: RequestOptions = { ...options, method: 'POST' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiClient<T>(endpoint, init);
};

apiClient.put = <T>(endpoint: string, body?: unknown, options?: RequestOptions) => {
  const init: RequestOptions = { ...options, method: 'PUT' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiClient<T>(endpoint, init);
};

apiClient.patch = <T>(endpoint: string, body?: unknown, options?: RequestOptions) => {
  const init: RequestOptions = { ...options, method: 'PATCH' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiClient<T>(endpoint, init);
};

apiClient.delete = <T>(endpoint: string, options?: RequestOptions) =>
  apiClient<T>(endpoint, { ...options, method: 'DELETE' });

/**
 * Authenticated fetch for non-JSON bodies (e.g. multipart). Retries once after session refresh on 401.
 */
export async function apiFetchWithAuth(
  endpoint: string,
  init: RequestInit = {},
  retryAfterRefresh = false
): Promise<Response> {
  const url = `${API_URL}${endpoint.startsWith('/') ? endpoint.substring(1) : endpoint}`;

  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getBearerTokenSync();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    credentials: 'include',
    cache: 'no-store',
    ...init,
    headers,
  });

  if (
    response.status === 401 &&
    !retryAfterRefresh &&
    typeof window !== 'undefined' &&
    !shouldSkipRefresh(endpoint, { _skipAuthRefresh: false })
  ) {
    const allowBootstrap = !useAuth.getState().isAuthenticated;
    const refreshed = await refreshBrowserSession({
      trigger: 'reactive',
      force: true,
      allowBootstrap,
    });
    if (refreshed) {
      return apiFetchWithAuth(endpoint, init, true);
    }

    const path = window.location.pathname;
    const isAuthPage = isAuthPagePath(path);
    const isOrgChooser = path === '/choose-organization';
    if (!isAuthPage && !isOrgChooser) {
      const { restoreSession } = await import('@/lib/session');
      if (await restoreSession()) {
        return apiFetchWithAuth(endpoint, init, true);
      }
    }
  }

  return response;
}

// Compatibility export
export const api = apiClient;
